import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RightOutlined, RocketOutlined, SyncOutlined } from "@ant-design/icons";

import LoanInsure from "../pages/LoanInsure";
import { fetchOvernightInternalBalances } from "../helpers/api";
import { isKredafiShell } from "../utils/brand";
import { usePublicRates, rateOr, demandRateOr } from "../hooks/usePublicRates";
import { RootState } from "../services/store";

export type KredafiPanel = "none" | "deposit" | "issue" | "borrow" | "withdraw";

export type OpenDepositOpts = { voucher?: boolean };

type CabinetContextValue = {
  panel: KredafiPanel;
  voucherMode: boolean;
  openDeposit: (opts?: OpenDepositOpts) => void;
  openIssue: () => void;
  openBorrow: () => void;
  openWithdraw: () => void;
  closePanel: () => void;
  assets: Record<string, number>;
  reloadAssets: () => Promise<void>;
};

const CabinetContext = createContext<CabinetContextValue | null>(null);

export function useKredafiCabinetOptional(): CabinetContextValue | null {
  return useContext(CabinetContext);
}

export function useKredafiCabinet(): CabinetContextValue {
  const ctx = useKredafiCabinetOptional();
  if (!ctx) {
    throw new Error("useKredafiCabinet must be used inside KredafiCabinetProvider");
  }
  return ctx;
}

export function KfSubpage({
  title,
  onClose,
  children,
}: {
  title: React.ReactNode;
  onClose?: () => void;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  if (!isKredafiShell()) {
    return <>{children}</>;
  }
  return (
    <div className="kf-subpage">
      <KfPanelHead title={title} onClose={onClose || (() => navigate("/settings"))} />
      {children}
    </div>
  );
}

export function KfPanelHead({
  title,
  onClose,
}: {
  title: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="kf-panel-head">
      <h2 className="kf-panel-title">{title}</h2>
      <button type="button" className="kf-x" onClick={onClose} aria-label="Close">
        ×
      </button>
    </div>
  );
}

export function KredafiBorrowPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="kf-panel">
      <KfPanelHead title={t("Take out a loan")} onClose={onClose} />
      <div className="kf-borrow-body">
        <LoanInsure inline />
      </div>
    </div>
  );
}

export function KredafiIssuePanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useSelector((state: RootState) => state.user);
  // Тот же запасной источник ставок, что и на экране выдачи: настройки из
  // add-user.php могут не доехать, и тогда карточки продуктов оставались без
  // процента вовсе.
  const publicRates = usePublicRates();
  const pick = (key: "max_rate_overnight" | "demand_rate_overnight") => {
    const fromAccount = Number((userData?.settings as any)?.[key]);
    if (Number.isFinite(fromAccount) && fromAccount > 0) return fromAccount;
    const fallback = rateOr(publicRates, key, 0);
    return fallback > 0 ? fallback : null;
  };
  const overnightRate = pick("max_rate_overnight");
  const demandFromAccount = pick("demand_rate_overnight");
  const demandRate =
    overnightRate && demandFromAccount && demandFromAccount > overnightRate
      ? demandFromAccount
      : overnightRate
      ? demandRateOr(publicRates, overnightRate)
      : demandFromAccount;

  const openLoan = (demand: boolean) => {
    try {
      sessionStorage.removeItem("kf_preferred_shelf");
    } catch {
      /* ignore */
    }
    navigate("/loan-overnight", { state: { demand } });
    onClose();
  };

  let preferredShelf = "";
  try {
    preferredShelf = sessionStorage.getItem("kf_preferred_shelf") || "";
  } catch {
    preferredShelf = "";
  }

  return (
    <div className="kf-panel">
      <KfPanelHead title={t("Place at current rate")} onClose={onClose} />
      <button type="button" className="kf-issue-card" onClick={() => openLoan(false)}>
        <span className="kf-issue-ico kf-issue-ico-night">
          <RocketOutlined />
        </span>
        <span className="kf-issue-copy">
          <span className="kf-issue-name">{t("Overnight shelf")}</span>
          <span className="kf-issue-desc">
            {overnightRate ? `${t("Rate")} ${overnightRate}% · ` : ""}
            {t("Overnight returns next morning")}
          </span>
        </span>
        <RightOutlined className="kf-issue-chevron" />
      </button>
      <button
        type="button"
        className={`kf-issue-card${preferredShelf === "demand" ? " kf-issue-card-pref" : ""}`}
        onClick={() => openLoan(true)}
      >
        <span className="kf-issue-ico kf-issue-ico-demand">
          <SyncOutlined />
        </span>
        <span className="kf-issue-copy">
          <span className="kf-issue-name">{t("On-demand shelf")}</span>
          <span className="kf-issue-desc">
            {demandRate ? `${t("Rate")} ${demandRate}% · ` : ""}
            {t("On-demand stop returns within 24h")}
          </span>
        </span>
        <RightOutlined className="kf-issue-chevron" />
      </button>
    </div>
  );
}

export const KredafiCabinetProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { userData } = useSelector((state: RootState) => state.user);
  const [panel, setPanel] = useState<KredafiPanel>("none");
  const [voucherMode, setVoucherMode] = useState(false);
  const [internalBalances, setInternalBalances] = useState<{
    usdt: number;
    ton: number;
  } | null>(null);
  const [extraAssets, setExtraAssets] = useState<Record<string, number>>({});

  useEffect(() => {
    try {
      const intent = sessionStorage.getItem("kf_after_login");
      if (!intent) return;
      sessionStorage.removeItem("kf_after_login");
      if (intent === "deposit") {
        setVoucherMode(false);
        setPanel("deposit");
      } else if (intent === "issue" || intent === "issue-demand") {
        setVoucherMode(false);
        setPanel("issue");
        if (intent === "issue-demand") {
          sessionStorage.setItem("kf_preferred_shelf", "demand");
        }
      }
    } catch {
      /* private mode */
    }
  }, []);

  const reloadAssets = useCallback(async () => {
    try {
      const res = await fetchOvernightInternalBalances();
      if (res.success) {
        setInternalBalances({
          usdt: res.balance_overnight_usdt,
          ton: res.balance_overnight_ton,
        });
        if (res.assets) setExtraAssets(res.assets);
      }
    } catch {
      /* сеть могла моргнуть — баланс подтянется следующим заходом */
    }
  }, []);

  useEffect(() => {
    reloadAssets();
    const onPfDone = () => reloadAssets();
    window.addEventListener("pf-withdraw-success", onPfDone);
    return () => window.removeEventListener("pf-withdraw-success", onPfDone);
  }, [reloadAssets]);

  const assets = useMemo<Record<string, number>>(
    () => ({
      USDT: Number(internalBalances?.usdt ?? userData?.balance_overnight_usdt ?? 0),
      TON: Number(internalBalances?.ton ?? userData?.balance_overnight_ton ?? 0),
      ...extraAssets,
    }),
    [internalBalances, extraAssets, userData?.balance_overnight_usdt, userData?.balance_overnight_ton]
  );

  const value = useMemo<CabinetContextValue>(
    () => ({
      panel,
      voucherMode,
      openDeposit: (opts?: OpenDepositOpts) => {
        setVoucherMode(!!opts?.voucher);
        setPanel("deposit");
      },
      openIssue: () => {
        setVoucherMode(false);
        setPanel("issue");
      },
      openBorrow: () => {
        setVoucherMode(false);
        setPanel("borrow");
      },
      openWithdraw: () => {
        setVoucherMode(false);
        setPanel("withdraw");
      },
      closePanel: () => {
        setVoucherMode(false);
        setPanel("none");
      },
      assets,
      reloadAssets,
    }),
    [panel, voucherMode, assets, reloadAssets]
  );

  return <CabinetContext.Provider value={value}>{children}</CabinetContext.Provider>;
};
